import {Modal} from 'ant-design-vue';
import dayjs from 'dayjs';
import vehicleFormStrings from '../../components/settings/vehicles/vehicle-form.strings.yaml';
import {UKey, setMetadata} from '../../core/data-source/data-source';
import {HttpCrudDataSource, RestResponseRecord} from '../../core/data-source/http-crud.data-source';
import {fromJson, toJson} from '../../core/data-source/json.mapper';
import {getStrings} from '../../core/lang';
import {IHttpRequestOptions, httpService} from '../../core/services/http.service';
import {DictionaryRecord} from '../../records/dictionary.record';
import {VehicleFilterRecord} from '../../records/vehicle-filter.record';
import {DriverRecord} from '../../records/driver.record';
import {VehicleRecord} from '../../records/vehicle.record';

export class VehicleCrudDataSource<T> extends HttpCrudDataSource<T> {
    protected apiPath: string = '/gt/gt-api/vehicles';

    async all(filter: VehicleFilterRecord, options?: IHttpRequestOptions): Promise<T[]> {
        return httpService.post(this.apiPath + '/list', toJson(filter), options).then((response: any) => {
            if (response.data.success) {
                return setMetadata(
                    response.data.rows.map((v: any) => fromJson(this.recordClass, v)),
                    fromJson(RestResponseRecord, response.data)
                );
            } else {
                throw response.data.errors;
            }
        });
    }

    async listAssignments(options?: IHttpRequestOptions): Promise<T[]> {
        return httpService.get(this.apiPath + '/assignments', options).then((response: any) => {
            return response.data.rows.map((v: any) => fromJson(this.recordClass, v));
        });
    }

    async checkVehicleName(name: string): Promise<boolean> {
        return httpService.get(this.apiPath + '/check_uniqueness_name?name=' + name).then((response: any) => {
            return response.data.success;
        });
    }

    async getTrackingProviders(): Promise<DictionaryRecord[]> {
        return httpService
            .get(this.apiPath + '/tracking/providers')
            .then((response: any) => response.data.rows.map((v: any) => fromJson(DictionaryRecord, v)));
    }

    async updateDefaults(record: T & {id: UKey}, options: IHttpRequestOptions = {}): Promise<T> {
        let resultRecord: T;
        try {
            resultRecord = await super.updateDefaults(record, Object.assign(options, {disableErrorHandling: true}));
        } catch (errors) {
            if (await this.removeSchedules(errors)) {
                if (!options || !options.params) {
                    options = {params: {}};
                }
                options.params.deleteSchedule = true;
                resultRecord = await super.updateDefaults(record, options);
            }
        }
        return resultRecord;
    }

    async update(record: T & {id: UKey}, options: IHttpRequestOptions = {}): Promise<T> {
        const S = getStrings<VehicleFormStrings & CommonStrings>([vehicleFormStrings]);
        let resultRecord: T;
        try {
            const driverCheckPromise = new Promise((resolve: Function) => {
                if (!record.id && !(record as any).driver) {
                    Modal.confirm({
                        centered: true,
                        title: S.confirmation,
                        okText: S.yes,
                        cancelText: S.no,
                        content: S.createDriverLabel,
                        onOk: () => {
                            resolve('CREATE');
                        },
                        onCancel: () => {
                            resolve('UNALLOCATE');
                        }
                    });
                } else {
                    resolve(false);
                }
            });
            const action = await driverCheckPromise;
            if (action) {
                (record as any).action = action;
            }
            resultRecord = await super.update(record, Object.assign(options, {disableErrorHandling: true}));
        } catch (errors) {
            if (await this.removeSchedules(errors)) {
                if (!options || !options.params) {
                    options = {params: {}};
                }
                options.params.deleteSchedule = true;
                resultRecord = await super.update(record, options);
            }
        }
        return resultRecord;
    }

    private async removeSchedules(errors: any[]) {
        const S = getStrings<VehicleFormStrings & CommonStrings>([vehicleFormStrings]);
        if (errors[0].code === 'RESOURCE_HAS_SCHEDULE') {
            let dates: string[] = errors[0].data.map((obj: any): string => dayjs.utc(obj['f1']).format('DD/MM/YYYY'));
            const deleteSchedules = await new Promise((resolve: Function) => {
                Modal.confirm({
                    content:
                        dates.length > 1
                            ? S.format('RESOURCE_HAS_SCHEDULE_WITH_DATES', dates.join(', '))
                            : dates.length === 1
                              ? S.format('RESOURCE_HAS_SCHEDULE_WITH_DATE', dates[0])
                              : S['RESOURCE_HAS_SCHEDULE'],
                    title: S.confirmation,
                    okText: S.ok,
                    cancelText: S.cancel,
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false)
                });
            });
            if (deleteSchedules) {
                return true;
            }
        } else if (errors[0]?.message) {
            Modal.confirm({
                content: errors[0].message,
                title: S.error
            });
        }
        return false;
    }

    async remove(ids: UKey[]): Promise<any> {
        const data = await httpService.delete(this.apiPath, {
            params: {
                ids: ids.join(',')
            },
            disableErrorHandling: true
        });
        if (data.data.errors) {
            if (await this.removeSchedules(data.data.errors)) {
                return httpService.delete(this.apiPath, {
                    params: {
                        ids: ids.join(','),
                        deleteSchedule: true
                    }
                });
            }
        }
    }
}
